import React, { useState, useEffect, useRef } from "react";

/**
 * SearchableRouteDropdown Component
 * 
 * A reusable dropdown component with search functionality for selecting routes
 * 
 * @param {Array} routes - Array of route objects with routeId and routeName properties
 * @param {Object} selectedRoute - Currently selected route object
 * @param {Function} setSelectedRoute - Callback function to update selected route
 * @param {Boolean} isLoading - Loading state to disable the input
 */
const SearchableRouteDropdown = ({ routes, selectedRoute, setSelectedRoute, isLoading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Update search term when selected route changes externally
    useEffect(() => {
        if (selectedRoute) {
            setSearchTerm(selectedRoute.routeName);
        } else {
            setSearchTerm('');
        }
    }, [selectedRoute]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter routes based on search term
    const filteredRoutes = routes.filter(route =>
        route.routeName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle route selection
    const handleSelect = (route) => {
        setSelectedRoute(route);
        setSearchTerm(route.routeName);
        setShowDropdown(false);
    };

    // Handle input change
    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setShowDropdown(true);

        // Clear selection if user modifies the text
        if (selectedRoute && e.target.value !== selectedRoute.routeName) {
            setSelectedRoute(null);
        }
    };

    // Handle clear button
    const handleClear = () => {
        setSearchTerm('');
        setSelectedRoute(null);
        setShowDropdown(false);
    };

    return (
        <div className="mb-3 position-relative" ref={dropdownRef}>
            <label htmlFor="routeSearch" className="form-label">
                Select Route <span className="text-danger">*</span>
            </label>
            <div className="input-group">
                <input
                    id="routeSearch"
                    type="text"
                    className="form-control"
                    placeholder="Search and select a route..."
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setShowDropdown(true)}
                    disabled={isLoading}
                    autoComplete="off"
                    style={{
                        paddingRight: searchTerm && !isLoading ? "70px" : "32px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                    }}
                />
                {searchTerm && !isLoading && (
                    <button
                        type="button"
                        onClick={handleClear}
                        title="Clear selection"
                        style={{
                            position: 'absolute',
                            right: '32px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            padding: 0,
                            transition: 'all 0.2s ease',
                            lineHeight: 1
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#c82333';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#dc3545';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        }}
                    >
                        ×
                    </button>
                )}
                <span
                    style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#6c757d",
                        fontSize: "14px"
                    }}
                >
                    <i className="fas fa-chevron-down" />
                </span>
            </div>

            {showDropdown && !isLoading && (
                <ul
                    className="list-group position-absolute w-100 shadow-lg"
                    style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1050,
                        marginTop: '2px'
                    }}
                >
                    {filteredRoutes.length === 0 && (
                        <li className="list-group-item text-muted text-center">
                            <i className="fas fa-search me-2"></i>
                            No routes found
                        </li>
                    )}
                    {filteredRoutes.map(route => (
                        <li
                            key={route.routeId}
                            className={`list-group-item list-group-item-action ${selectedRoute?.routeId === route.routeId ? 'active' : ''
                                }`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelect(route)}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <span>{route.routeName}</span>
                                {selectedRoute?.routeId === route.routeId && (
                                    <i className="fas fa-check text-white"></i>
                                )}
                            </div>
                            {/* {route.fromLocation && route.toLocation && (
                                <small className="text-muted d-block">
                                    {route.fromLocation} → {route.toLocation}
                                </small>
                            )} */}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default SearchableRouteDropdown;